import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../contexts/AuthContext"
import type { Photo } from "../types"

interface CameraUploadProps {
  spotId: string
  onUploaded: (photo: Photo) => void
}

export default function CameraUpload({ spotId, onUploaded }: CameraUploadProps) {
  const { t } = useTranslation()
  const { auth } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [caption, setCaption] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)

  const API_URL = import.meta.env.VITE_API_URL || "/api"

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setError(null)
    const reader = new FileReader()
    reader.onloadend = () => setPreview(reader.result as string)
    reader.readAsDataURL(f)
  }

  const handleUpload = async () => {
    if (!file || !auth.token) return
    setLoading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append("spot_id", spotId)
      formData.append("file", file)
      if (caption.trim()) formData.append("caption", caption.trim())

      const res = await fetch(`${API_URL}/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` },
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Upload failed")
      }
      const photo = await res.json()
      onUploaded(photo)
      setPreview(null)
      setCaption("")
      setFile(null)
      if (inputRef.current) inputRef.current.value = ""
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*;capture=camera"
        onChange={handleFileChange}
        className="hidden"
      />
      {!preview ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
        >
          {t("spot.takePhoto")}
        </button>
      ) : (
        <div className="space-y-2">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-xl"
          />
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t("spot.photoCaptionPlaceholder")}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
          />
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={loading}
              className="flex-1 py-2 rounded-lg bg-steel text-white text-sm font-medium hover:bg-steel-dark disabled:opacity-50"
            >
              {loading ? t("spot.uploading") : t("spot.upload")}
            </button>
            <button
              onClick={() => {
                setPreview(null)
                setFile(null)
                setCaption("")
                if (inputRef.current) inputRef.current.value = ""
              }}
              className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 text-sm"
            >
              {t("spot.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
